import { useCallback, useMemo } from "react";
import { useSelectOptionsFromSlug } from "./useSelectOptionsFromSlug";
import { Referral, UserTypes } from "../models";
import { Pronouns } from "../models/utility/Pronouns";
import { getValueOrBlank } from "../helper-functions/getValueOrBlank";
import { BLANK } from "../config/constants";
import { useAccountQuery, useTerritoryQuery } from "../queries";
import {
    displayDate,
    displayDateAndTime,
    displayTime,
    displayTimeZonelessDateAndTime,
    getFormattedPhoneNumber,
    isNullOrUndefined,
} from "../helper-functions";
import { useUserSelectOptions } from "./useUserSelectOptions";
import { useAccountSelectOptions } from "./useAccountSelectOptions";

export const useReferralFormData = (referral: Referral | undefined) => {
    //#region  Hospital Info
    const referralUserOptions = useUserSelectOptions([UserTypes.ReferralCoordinator, UserTypes.ReferralLead]);
    const referralTakenBy = useMemo(() => {
        const ret = referralUserOptions.find((x) => x.value === referral?.thirdPartyCallerLastName)?.label;
        return getValueOrBlank(ret);
    }, [referral?.thirdPartyCallerLastName, referralUserOptions]);

    const referralDateTime = useMemo(() => {
        return `${getValueOrBlank(displayDate(referral?.detail?.data?.referredOnDate))} ${getValueOrBlank(displayTime(referral?.detail?.data?.referredOnTime))}`;
    }, [referral?.detail?.data?.referredOnDate, referral?.detail?.data?.referredOnTime]);
    const callerFirstName = getValueOrBlank(referral?.detail?.data?.callerFirstName);
    const callerLastName = getValueOrBlank(referral?.detail?.data?.callerLastName);
    const callerTitleOptions = useSelectOptionsFromSlug("caller");
    const callerTitle = getValueOrBlank(
        callerTitleOptions.find((x) => x.value === referral?.detail?.data?.callerTitle)?.label
    );
    const {
        listQuery: { data: territories },
    } = useTerritoryQuery();
    const {
        detailQuery: { data: account },
    } = useAccountQuery({
        id:
            referral?.referringOrganizationId && !isNaN(Number(referral?.referringOrganizationId))
                ? referral?.referringOrganizationId
                : undefined,
    });
    const territoryOptions = territories?.map((t) => ({ label: t.name, value: t.id })) ?? [];
    const referralOrigin = getValueOrBlank(territoryOptions.find((x) => x.value === referral?.territoryId)?.label);
    const callerPhone = getFormattedPhoneNumber(referral?.detail?.data?.callerPhone);
    const referralPhone = getValueOrBlank(callerPhone);
    const referralPhoneExt = getValueOrBlank(referral?.detail?.data?.callerExtension);
    const hospitalName = account?.name ?? "";
    const unitOptions = account?.accountUnits?.map((u) => ({ label: u.name, value: u.aocCode })) ?? [];
    const referringOrganizationUnitName = getValueOrBlank(
        unitOptions.find((x) => x.value === referral?.referringOrganizationUnitName)?.label
    );
    const referringOrganizationUnitRoom = getValueOrBlank(referral?.referringOrganizationUnitDetail);
    const hspTransferUnitName = getValueOrBlank(
        unitOptions.find((x) => x.value === referral?.detail?.data?.hospitalTransferUnit)?.label
    );
    const hspTransferUnitRoom = getValueOrBlank(referral?.detail?.data?.hospitalTransferUnitDetail);
    const hspInfoSpecialInstructions = getValueOrBlank(account?.notes);
    const firstResponderOrganization = getValueOrBlank(referral?.detail?.data?.firstResponderOrganizationId);
    const parish = getValueOrBlank(account?.county);
    const region = getValueOrBlank(account?.office?.territory?.name);
    //#endregion

    const nokRelationshipOptions = useSelectOptionsFromSlug("nok_relationship_type");
    const genderOptions = useSelectOptionsFromSlug("gender");
    const patientGender = genderOptions.find((x) => x.value === referral?.patientGender)?.label;

    const authorizingPersons = useMemo(
        () => referral?.detail?.data?.nextOfKin ?? [],
        [referral?.detail?.data?.nextOfKin]
    );
    const nokRelationship = authorizingPersons[0]?.relationship
        ? nokRelationshipOptions.find((x) => x.value === authorizingPersons[0]?.relationship)?.label
        : BLANK;
    const nokRelationshipOther = getValueOrBlank(authorizingPersons[0]?.relationshipOther);
    const nok2Relationship = authorizingPersons[1]?.relationship
        ? nokRelationshipOptions.find((x) => x.value === authorizingPersons[1]?.relationship)?.label
        : BLANK;
    const nok2RelationshipOther = getValueOrBlank(authorizingPersons[1]?.relationshipOther);

    const formatAddressLine = (addrLine1: string, addrLine2: string): string => {
        if (addrLine1 === BLANK && addrLine2 === BLANK) {
            return BLANK;
        } else if (addrLine1 === BLANK) {
            return addrLine2;
        } else if (addrLine2 === BLANK) {
            return addrLine1;
        } else {
            return `${addrLine1} ${addrLine2}`;
        }
    };
    const getPronoun = useCallback(
        (pronoun: Pronouns): string => {
            switch (pronoun) {
                case Pronouns.Objective:
                    return patientGender ? (patientGender === "Male" ? "him" : "her") : "him/her";
                case Pronouns.Subjective:
                    return patientGender ? (patientGender === "Male" ? "he" : "she") : "he/she";
                case Pronouns.Possessive:
                    return patientGender ? (patientGender === "Male" ? "his" : "her") : "his/her";
            }
        },
        [patientGender]
    );

    const donorTitle = useMemo((): string => {
        switch (patientGender) {
            case "Female":
                return "Ms.";
            case "Male":
                return "Mr.";
            default:
                return "Mr./Ms.";
        }
    }, [patientGender]);
    const donorAgeUnitOptions = useSelectOptionsFromSlug("age_units");
    const donorRaceOptions = useSelectOptionsFromSlug("race");
    const donorHeightUnitOptions = useSelectOptionsFromSlug("height_unit");
    const donorWeightUnitOptions = useSelectOptionsFromSlug("weight_unit");
    const donorRegistrationStatusOptions = useSelectOptionsFromSlug("registry");
    const donorCauseOfDeathOptions = useSelectOptionsFromSlug("cause_of_death");
    const donorDeathTypeOptions = useSelectOptionsFromSlug("death_type");
    const donorTimeOfDeathTimezoneOptions = useSelectOptionsFromSlug("time_zone");
    const yesNoOptions = useSelectOptionsFromSlug("yes_no");
    const donorDownTimeInfoOptions = useSelectOptionsFromSlug("down_time_information_type");
    const yesNoUnknownOptions = useSelectOptionsFromSlug("yes_no_unknown");
    const salutationOptions = useSelectOptionsFromSlug("salutation");
    const preOrPostMortemTypes = useSelectOptionsFromSlug("pre_post_mortem_type");
    const initialMentionByOptions = useSelectOptionsFromSlug("initial_mention");
    const initialMentionModeOptions = useSelectOptionsFromSlug("mode");
    const approachInitialMentionFamilyResponseOptions = useSelectOptionsFromSlug("initial_response_type");
    const consentNotRequestedReasonOptions = useSelectOptionsFromSlug("authorization_not_requested_reason");
    const approachTypeOptions = useSelectOptionsFromSlug("approach_type");
    const approachModeOptions = useSelectOptionsFromSlug("approach_mode");
    const formalRequestByOptions = useSelectOptionsFromSlug("formal_request_by");
    const timeZoneOptions = useSelectOptionsFromSlug("time_zone");
    const eyeOutcomeOptions = useSelectOptionsFromSlug("eye_outcome");
    const tissueOutcomeOptions = useSelectOptionsFromSlug("tissue_outcome");
    const { options: coronerOptions } = useAccountSelectOptions("Coroner");

    const donorObjectivePronoun = getPronoun(Pronouns.Objective);
    const donorSubjectivePronoun = getPronoun(Pronouns.Subjective);
    const donorPossessivePronoun = getPronoun(Pronouns.Possessive);
    const donorFirstName = getValueOrBlank(referral?.patientFirstName);
    const donorMiddleName = getValueOrBlank(referral?.patientMiddleName);
    const donorLastName = getValueOrBlank(referral?.patientLastName);
    const donorFirstLastName = `${donorFirstName}${" "}${donorLastName}`;
    const donorFullName = `${donorFirstName}${" "}${donorMiddleName}${" "}${donorLastName}`;
    const donorDOB = getValueOrBlank(displayDate(referral?.patientBirthDate));
    const donorAge = getValueOrBlank(referral?.patientAge?.toString());
    const donorAgeUnit = getValueOrBlank(donorAgeUnitOptions.find((x) => x.value === referral?.patientAgeUnits)?.label);
    const donorGender = getValueOrBlank(patientGender);
    const donorRace = getValueOrBlank(donorRaceOptions.find((x) => x.value === referral?.patientRace)?.label);
    const donorHeight = getValueOrBlank(referral?.detail?.data?.patientHeightValue?.toString());
    const donorHeightUnit = getValueOrBlank(
        donorHeightUnitOptions.find((x) => x.value === referral?.detail?.data?.patientHeightUnits)?.label
    );
    const donorWeight = getValueOrBlank(referral?.detail?.data?.patientWeightValue?.toString());
    const donorWeightUnit = getValueOrBlank(
        donorWeightUnitOptions.find((x) => x.value === referral?.detail?.data?.patientWeightUnits)?.label
    );
    const donorRegistrationStatus = getValueOrBlank(
        donorRegistrationStatusOptions.find((x) => x.value === referral?.donorDesignationStatus)?.label
    );
    const donorRebisNum = getValueOrBlank(referral?.detail?.data?.rebisNumber);
    const donorMrn = getValueOrBlank(referral?.patientMrn);
    const donorAdmissionDate = getValueOrBlank(displayDate(referral?.detail?.data?.admittedOnDate));
    const donorAdmissionTime = getValueOrBlank(displayTime(referral?.detail?.data?.admittedOnTime));
    const donorDaysOnVent = getValueOrBlank(referral?.detail?.data?.daysOnVent?.toString());
    const donorCauseOfDeath = getValueOrBlank(
        donorCauseOfDeathOptions.find((x) => x.value === referral?.detail?.data?.specificCauseOfDeath)?.label
    );
    const donorCauseOfDeathOther = referral?.detail?.data?.specificCauseOfDeathOther;
    const donorAdmissionDiagnosis = getValueOrBlank(referral?.detail?.data?.admissionDiagnosis);
    const donorAttendingMd = getValueOrBlank(referral?.attendingMd);
    const donorReferralComments = getValueOrBlank(referral?.detail?.data?.donorReferralComments);
    const donorDeathType = getValueOrBlank(donorDeathTypeOptions.find((x) => x.value === referral?.deathType)?.label);
    const donorTimeOfDeathDateAndTime = getValueOrBlank(
        displayTimeZonelessDateAndTime(referral?.detail?.data?.timeOfDeathDate, referral?.detail?.data?.timeOfDeathTime)
    );
    const donorTimeOfDeathTimezone = getValueOrBlank(
        donorTimeOfDeathTimezoneOptions.find((x) => x.value === referral?.detail?.data?.timeOfDeathTimeZone)?.label
    );
    const donorMeCoronerCase = getValueOrBlank(yesNoOptions.find((x) => x.value === referral?.meCoronerCase)?.label);
    const donorNokParish = getValueOrBlank(referral?.detail?.data?.nokParish);
    const donorDownTimeInfo = getValueOrBlank(
        donorDownTimeInfoOptions.find((x) => x.value === referral?.detail?.data?.downTimeInformation)?.label
    );
    const donorDownTimeDuration = getValueOrBlank(referral?.detail?.data?.downTimeDuration?.toString());
    const donorHoldBodyContacted = getValueOrBlank(
        yesNoOptions.find((x) => x.value === referral?.detail?.data?.holdBodyContacted)?.label
    );
    const donorHoldBodyContactName = referral?.detail?.data?.holdBodyContactName;
    const donorScreeningLastDocumentedDate = getValueOrBlank(
        displayDate(referral?.detail?.data?.donorLastDocumentedDate)
    );
    const donorScreeningLastDocumentedTime = getValueOrBlank(
        displayTime(referral?.detail?.data?.donorLastDocumentedTime)
    );
    const donorScreeningLastDocumentedNote = getValueOrBlank(referral?.detail?.data?.donorLastDocumentedNote);

    const donorScreeningMedicalHistory = getValueOrBlank(referral?.detail?.data?.donorScreeningMedicalHistory);
    const donorScreeningMedications = getValueOrBlank(referral?.detail?.data?.donorScreeningMedications);
    const donorScreeningAntibiotics = getValueOrBlank(referral?.detail?.data?.donorScreeningAntibiotics);
    const donorScreeningHivHistory = getValueOrBlank(
        yesNoUnknownOptions.find((x) => x.value === referral?.detail?.data?.hivDiseaseHistory)?.label
    );
    const donorScreeningHbvHistory = getValueOrBlank(
        yesNoUnknownOptions.find((x) => x.value === referral?.detail?.data?.hbvDiseaseHistory)?.label
    );
    const donorScreeningHcvHistory = getValueOrBlank(
        yesNoUnknownOptions.find((x) => x.value === referral?.detail?.data?.hcvDiseaseHistory)?.label
    );
    const donorScreeningIvDrugHistory = getValueOrBlank(
        yesNoUnknownOptions.find((x) => x.value === referral?.detail?.data?.ivdaDiseaseHistory)?.label
    );
    const donorScreeningHighRiskHistory = getValueOrBlank(
        yesNoUnknownOptions.find((x) => x.value === referral?.detail?.data?.highRiskDonorDiseaseHistory)?.label
    );
    const donorScreeningDiabetesHistory = getValueOrBlank(
        yesNoUnknownOptions.find((x) => x.value === referral?.detail?.data?.diabeteseDiseaseHistory)?.label
    );
    const donorScreeningNeuroHistory = getValueOrBlank(
        yesNoUnknownOptions.find((x) => x.value === referral?.detail?.data?.neurologicalDiseaseHistory)?.label
    );
    const donorScreeningCancerHistory = getValueOrBlank(
        yesNoUnknownOptions.find((x) => x.value === referral?.detail?.data?.cancerDiseaseHistory)?.label
    );
    const donorScreeningMrsaHistory = getValueOrBlank(
        yesNoUnknownOptions.find((x) => x.value === referral?.detail?.data?.antibioticResistantBacterialHistory)?.label
    );
    const donorScreeningIsolationHistory = getValueOrBlank(
        yesNoUnknownOptions.find((x) => x.value === referral?.detail?.data?.isolationDiseaseHistory)?.label
    );
    const donorScreeningPotentialTissueIssue = getValueOrBlank(
        yesNoUnknownOptions.find((x) => x.value === referral?.detail?.data?.potentialTissueIssue)?.label
    );
    const donorScreeningPotentialTissueIssueNote = getValueOrBlank(referral?.detail?.data?.potentialTissueIssueNote);
    const donorScreeningSignsOfInfection = getValueOrBlank(
        yesNoOptions.find((x) => x.value === referral?.detail?.data?.systemicInfectionSigns)?.label
    );
    const donorScreeningSignsOfInfectionNotes = referral?.detail?.data?.systemicInfectionNote;
    const donorScreeningActiveSepsis = getValueOrBlank(
        yesNoOptions.find((x) => x.value === referral?.detail?.data?.activeSepsis)?.label
    );
    const donorScreeningActiveSepsisNotes = referral?.detail?.data?.activeSepsisNote;
    const mePreOrPostMortem = getValueOrBlank(
        preOrPostMortemTypes.find((x) => x.value === referral?.detail?.data?.meCoronerContactedPrePost)?.label
    );
    const meContactedOnDate = getValueOrBlank(displayDate(referral?.detail?.data?.meCoronerContactedOnDate));
    const meContactedOnTime = getValueOrBlank(displayTime(referral?.detail?.data?.meCoronerContactedOnTime));
    const mePrePostMortemDate = `${getValueOrBlank(displayDate(referral?.detail?.data?.meCoronerContactedOnDate))} ${getValueOrBlank(displayTime(referral?.detail?.data?.meCoronerContactedOnTime))}`;
    const mePreOrPostMortemLabel = `${mePreOrPostMortem} ${mePrePostMortemDate}`;
    const meOffice = getValueOrBlank(
        coronerOptions.find((x) => x.value === referral?.detail?.data?.meCoronerOrganizationName)?.label
    );

    const nokSalutation = getValueOrBlank(authorizingPersons[0]?.salutation);
    const nokFirstName = getValueOrBlank(authorizingPersons[0]?.firstName);
    const nokLastName = getValueOrBlank(authorizingPersons[0]?.lastName);
    const nokName = `${getValueOrBlank(authorizingPersons[0]?.firstName)}${" "}${getValueOrBlank(authorizingPersons[0]?.lastName)}`;
    const nokRelationToDonor = useMemo(() => {
        return nokRelationship === "Other" ? nokRelationshipOther : nokRelationship;
    }, [nokRelationship, nokRelationshipOther]);
    const nokAddress = formatAddressLine(
        getValueOrBlank(authorizingPersons[0]?.addressLine1),
        getValueOrBlank(authorizingPersons[0]?.addressLine2)
    );
    const nokCity = getValueOrBlank(authorizingPersons[0]?.addressCity);
    const nokState = getValueOrBlank(authorizingPersons[0]?.addressStateCode);
    const nokZip = getValueOrBlank(authorizingPersons[0]?.addressPostalCode);
    const nokPhone = getValueOrBlank(getFormattedPhoneNumber(authorizingPersons[0]?.phone));
    const nokAlternatePhone = getValueOrBlank(getFormattedPhoneNumber(authorizingPersons[0]?.phone2));
    const nokEmailAddress = getValueOrBlank(authorizingPersons[0]?.email);

    const nokAwareOfLocationOfBody = getValueOrBlank(authorizingPersons[0]?.awareOfLocationOfBody);
    const nokNotifiedOfDeath = getValueOrBlank(authorizingPersons[0]?.authorizingPersonNotifiedOfDeath);

    const nok2FirstName = getValueOrBlank(authorizingPersons[1]?.firstName);
    const nok2LastName = getValueOrBlank(authorizingPersons[1]?.lastName);
    const nok2Name = `${getValueOrBlank(authorizingPersons[1]?.firstName)}${" "}${getValueOrBlank(authorizingPersons[0]?.lastName)}`;
    const nok2RelationToDonor = useMemo(() => {
        return nok2Relationship === "Other" ? nok2RelationshipOther : nok2Relationship;
    }, [nok2Relationship, nok2RelationshipOther]);
    const nok2Address = formatAddressLine(
        getValueOrBlank(authorizingPersons[1]?.addressLine1),
        getValueOrBlank(authorizingPersons[1]?.addressLine2)
    );
    const nok2City = getValueOrBlank(authorizingPersons[1]?.addressCity);
    const nok2State = getValueOrBlank(authorizingPersons[1]?.addressStateCode);
    const nok2Zip = getValueOrBlank(authorizingPersons[1]?.addressPostalCode);
    const nok2Phone = getValueOrBlank(getFormattedPhoneNumber(authorizingPersons[1]?.phone));
    const nok2AlternatePhone = getValueOrBlank(getFormattedPhoneNumber(authorizingPersons[1]?.phone2));
    const facilityHospital = getValueOrBlank(account?.name);
    const referralCreatedOn = getValueOrBlank(displayDateAndTime(referral?.createdOn));

    const {
        detailQuery: { data: funeralHomeAccount },
    } = useAccountQuery({ id: referral?.detail?.data?.funeralHome });
    const isFuneralHomeOther = referral?.detail?.data?.funeralHome === "Other";
    let funeralHome = isFuneralHomeOther
        ? getValueOrBlank(referral?.detail?.data?.funeralHomeOther)
        : getValueOrBlank(funeralHomeAccount?.name);

    const funeralHomeCity = getValueOrBlank(
        isFuneralHomeOther ? referral?.detail?.data?.ocFuneralHomeCity : funeralHomeAccount?.city
    );
    const funeralHomePhone = getValueOrBlank(
        isFuneralHomeOther ? referral?.detail?.data?.funeralHomePhone : funeralHomeAccount?.phone
    );
    const ocularTeam = getValueOrBlank(referral?.ocularTeam);
    const ocularTeamDateTime = useMemo(() => {
        return `${getValueOrBlank(displayDate(referral?.detail?.data?.ocularTeamDate))} ${getValueOrBlank(displayTime(referral?.detail?.data?.ocularTeamTime))}`;
    }, [referral?.detail?.data?.ocularTeamDate, referral?.detail?.data?.ocularTeamTime]);
    const ocularTeamAcknowledged = isNullOrUndefined(referral?.detail?.data?.ocularTeamAcknowledged)
        ? false
        : referral?.detail?.data?.ocularTeamAcknowledged;
    const ocularTeamAcknowledgedDateTime = useMemo(() => {
        return `${getValueOrBlank(displayDate(referral?.detail?.data?.ocularTeamAcknowledgedDate))} ${getValueOrBlank(displayTime(referral?.detail?.data?.ocularTeamAcknowledgedTime))}`;
    }, [referral?.detail?.data?.ocularTeamAcknowledgedDate, referral?.detail?.data?.ocularTeamAcknowledgedTime]);
    const familyStatus = getValueOrBlank(referral?.detail?.data?.familyStatus);
    const familyStatusUpdatedBy = getValueOrBlank(referral?.detail?.data?.familyStatusUpdatedBy);
    const familyStatusUpdatedOn = getValueOrBlank(displayDateAndTime(referral?.detail?.data?.familyStatusUpdatedOn));
    const organStatus = getValueOrBlank(referral?.detail?.data?.organStatus);
    const organStatusUpdatedBy = getValueOrBlank(referral?.detail?.data?.organStatusUpdatedBy);
    const organStatusUpdatedOn = getValueOrBlank(displayDateAndTime(referral?.detail?.data?.organStatusUpdatedOn));
    const tissueStatus = getValueOrBlank(referral?.detail?.data?.tissueStatus);
    const tissueStatusUpdatedBy = getValueOrBlank(referral?.detail?.data?.tissueStatusUpdatedBy);
    const tissueStatusUpdatedOn = getValueOrBlank(displayDateAndTime(referral?.detail?.data?.tissueStatusUpdatedOn));
    const disableImport = isNullOrUndefined(referral?.detail?.data?.disableImport)
        ? false
        : referral?.detail?.data?.disableImport;
    const pendingDfsRnUpdates = isNullOrUndefined(referral?.pendingDfsRnUpdates)
        ? false
        : referral?.pendingDfsRnUpdates;
    const hideFromDashboard = isNullOrUndefined(referral?.hideFromDashboard) ? false : referral?.hideFromDashboard;
    const callNotes = useMemo(() => {
        if (!referral?.detail?.data?.callNotes) return [];
        return referral?.detail?.data?.callNotes.map((note) => ({
            ...note,
            details: getValueOrBlank(note.details),
            contactName: getValueOrBlank(note.contactName),
            contactedOn: displayDateAndTime(note.contactedOn),
            createdOn: displayDateAndTime(note.createdOn),
        }));
    }, [referral?.detail?.data?.callNotes]);

    const ocMorgueDate = getValueOrBlank(displayDate(referral?.detail?.data?.ocMorgueDate));
    const funeralHomeContactedOnDate = getValueOrBlank(displayDate(referral?.detail?.data?.funeralHomeContactedOnDate));
    const funeralHomeContactedOnTime = getValueOrBlank(displayTime(referral?.detail?.data?.funeralHomeContactedOnTime));
    const recentAlbuminGiven = getValueOrBlank(
        yesNoOptions.find((x) => x.value === referral?.detail?.data?.recentAlbuminGiven)?.label
    );
    const recentAlbuminAmount = getValueOrBlank(referral?.detail?.data?.recentAlbuminAmount);
    const recentIVMedsGiven = getValueOrBlank(
        yesNoOptions.find((x) => x.value === referral?.detail?.data?.recentIVMedsGiven)?.label
    );
    const recentIVMedsAmount = getValueOrBlank(referral?.detail?.data?.recentIVMedsAmount);
    const recentIVFluidGiven = getValueOrBlank(
        yesNoOptions.find((x) => x.value === referral?.detail?.data?.recentIVFluidGiven)?.label
    );
    const recentIVFluidAmount = getValueOrBlank(referral?.detail?.data?.recentIVFluidAmount);
    const recentBloodGiven = getValueOrBlank(
        yesNoOptions.find((x) => x.value === referral?.detail?.data?.recentBloodGiven)?.label
    );
    const recentBloodAmount = getValueOrBlank(referral?.detail?.data?.recentBloodAmount);
    const plasmaLast48Hours = getValueOrBlank(
        yesNoOptions.find((x) => x.value === referral?.detail?.data?.plasmaLast48Hours)?.label
    );
    const recentPlasmaAmount = getValueOrBlank(referral?.detail?.data?.recentPlasmaAmount);
    const prelimComments = getValueOrBlank(referral?.detail?.data?.prelimComments);
    const transfusedRbcAmount = getValueOrBlank(referral?.detail?.data?.transfusedRbcAmount);
    const transfusedWholeBloodAmount = getValueOrBlank(referral?.detail?.data?.transfusedWholeBloodAmount);
    const transfusedOtherAmount = getValueOrBlank(referral?.detail?.data?.transfusedOtherAmount);
    const colloidsHespanAmount = getValueOrBlank(referral?.detail?.data?.colloidsHespanAmount);
    const colloidsFfpAmount = getValueOrBlank(referral?.detail?.data?.colloidsFfpAmount);
    const colloidsPlateletsAmount = getValueOrBlank(referral?.detail?.data?.colloidsPlateletsAmount);
    const colloidsCryoAmount = getValueOrBlank(referral?.detail?.data?.colloidsCryoAmount);
    const colloidsAlbumin5Amount = getValueOrBlank(referral?.detail?.data?.colloidsAlbumin5Amount);
    const colloidsAlbumin25Amount = getValueOrBlank(referral?.detail?.data?.colloidsAlbumin25Amount);
    const colloidsOtherAmount = getValueOrBlank(referral?.detail?.data?.colloidsOtherAmount);
    const crystalloidsAmount = getValueOrBlank(referral?.detail?.data?.crystalloidsAmount);
    const crystalloidsOtherAmount = getValueOrBlank(referral?.detail?.data?.crystalloidsOtherAmount);
    const initialMentionBy = getValueOrBlank(
        initialMentionByOptions.find((x) => x.value === referral?.detail?.data?.initialMentionBy)?.label
    );
    const initialMentionMode = getValueOrBlank(
        initialMentionModeOptions.find((x) => x.value === referral?.detail?.data?.initialMentionMode)?.label
    );
    const approachInitialMentionFamilyResponse = getValueOrBlank(
        approachInitialMentionFamilyResponseOptions.find(
            (x) => x.value === referral?.detail?.data?.approachInitialMentionFamilyResponse
        )?.label
    );
    const initatedByFirstName = getValueOrBlank(referral?.detail?.data?.initiatedByFirstName);
    const initiatedByLastName = getValueOrBlank(referral?.detail?.data?.initiatedByLastName);
    const initiatedByTitle = getValueOrBlank(referral?.detail?.data?.initiatedByTitle);
    const initiatedOnDateTime = useMemo(() => {
        return `${getValueOrBlank(displayDate(referral?.detail?.data?.initiatedOnDate))} ${getValueOrBlank(displayTime(referral?.detail?.data?.initiatedOnTime))}`;
    }, [referral?.detail?.data?.initiatedOnDate, referral?.detail?.data?.initiatedOnTime]);
    const initialMentionRestrictions = getValueOrBlank(referral?.detail?.data?.initialMentionRestrictions);
    const initialMentionNa = isNullOrUndefined(referral?.detail?.data?.initialMentionNa)
        ? false
        : referral?.detail?.data?.initialMentionNa;
    const initialMentionComments = getValueOrBlank(referral?.detail?.data?.initialMentionComments);
    const highRiskNotes = getValueOrBlank(referral?.detail?.data?.highRiskNotes);
    const consentNotRequested = isNullOrUndefined(referral?.consentNotRequested)
        ? false
        : referral?.consentNotRequested;
    const consentNotRequestedReason = getValueOrBlank(
        consentNotRequestedReasonOptions.find((x) => x.value === referral?.detail?.data?.consentNotRequestedReason)
            ?.label
    );
    const approachType = getValueOrBlank(
        approachTypeOptions.find((x) => x.value === referral?.detail?.data?.approachType)?.label
    );
    const approachMode = getValueOrBlank(
        approachModeOptions.find((x) => x.value === referral?.detail?.data?.approachMode)?.label
    );
    const formalRequestBy = getValueOrBlank(
        formalRequestByOptions.find((x) => x.value === referral?.detail?.data?.formalRequestBy)?.label
    );
    const formalRequestTitle = getValueOrBlank(referral?.detail?.data?.formalRequestByTitle);
    const requestMadeByOther =
        formalRequestBy === "Call Center"
            ? getValueOrBlank(
                  referralUserOptions.find((x) => x.value === referral?.detail?.data?.requestMadeByOther)?.label
              )
            : getValueOrBlank(referral?.detail?.data?.requestMadeByOther);
    const formalRequestOnTimeZone = getValueOrBlank(
        timeZoneOptions.find((x) => x.value === referral?.detail?.data?.formalRequestOnTimeZone)?.label
    );
    const formalRequestDateTime = useMemo(() => {
        return `${getValueOrBlank(displayDate(referral?.detail?.data?.formalRequestOnDate))} ${getValueOrBlank(displayTime(referral?.detail?.data?.formalRequestOnTime))} ${getValueOrBlank(formalRequestOnTimeZone)}`;
    }, [
        referral?.detail?.data?.formalRequestOnDate,
        referral?.detail?.data?.formalRequestOnTime,
        formalRequestOnTimeZone,
    ]);
    const formalRequestRestrictions = getValueOrBlank(referral?.detail?.data?.formalRequestRestrictions);
    const formalRequestCommentsNA = isNullOrUndefined(referral?.detail?.data?.formalRequestCommentsNA)
        ? false
        : referral?.detail?.data?.formalRequestCommentsNA;
    const formalRequestComments = getValueOrBlank(referral?.detail?.data?.formalRequestComments);
    const tissueOutcome = getValueOrBlank(tissueOutcomeOptions.find((x) => x.value === referral?.tissueOutcome)?.label);
    const tissueOutcomeDetail = getValueOrBlank(
        tissueOutcomeOptions.find((x) => x.value === referral?.detail?.data?.tissueOutcomeDetail)?.label
    );
    const eyeOutcome = getValueOrBlank(
        eyeOutcomeOptions.find((x) => x.value === referral?.detail?.data?.eyeOutcome)?.label
    );

    const isReferredOnVent =
        referral?.detail?.data?.referredOnVent === yesNoOptions.find((x) => x.label === "Yes")?.value;
    const donorReferredOnVent = getValueOrBlank(
        yesNoOptions.find((x) => x.value === referral?.detail?.data?.referredOnVent)?.label
    );
    const donorVented24HrsPrior = getValueOrBlank(
        yesNoOptions.find((x) => x.value === referral?.detail?.data?.vented24HourPrior)?.label
    );

    return {
        referralTakenBy,
        referralDateTime,
        callerFirstName,
        callerLastName,
        callerTitle,
        referralOrigin,
        referralPhone,
        referralPhoneExt,
        hospitalName,
        account,
        referringOrganizationUnitName,
        referringOrganizationUnitRoom,
        hspTransferUnitName,
        hspTransferUnitRoom,
        hspInfoSpecialInstructions,
        firstResponderOrganization,
        parish,
        region,
        donorTitle,
        donorObjectivePronoun,
        donorSubjectivePronoun,
        donorPossessivePronoun,
        donorFirstName,
        donorMiddleName,
        donorLastName,
        donorFirstLastName,
        donorFullName,
        donorDOB,
        donorAge,
        donorAgeUnit,
        donorGender,
        donorRace,
        donorHeight,
        donorHeightUnit,
        donorWeight,
        donorWeightUnit,
        donorRegistrationStatus,
        donorRebisNum,
        donorMrn,
        donorAdmissionDate,
        donorAdmissionTime,
        donorDaysOnVent,
        donorCauseOfDeath,
        donorCauseOfDeathOther,
        donorAdmissionDiagnosis,
        donorAttendingMd,
        donorReferralComments,
        donorDeathType,
        donorTimeOfDeathDateAndTime,
        donorTimeOfDeathTimezone,
        donorMeCoronerCase,
        donorNokParish,
        donorDownTimeInfo,
        donorDownTimeDuration,
        donorHoldBodyContacted,
        donorHoldBodyContactName,
        donorScreeningMedicalHistory,
        donorScreeningMedications,
        donorScreeningAntibiotics,
        donorScreeningHivHistory,
        donorScreeningHbvHistory,
        donorScreeningHcvHistory,
        donorScreeningIvDrugHistory,
        donorScreeningHighRiskHistory,
        donorScreeningDiabetesHistory,
        donorScreeningNeuroHistory,
        donorScreeningCancerHistory,
        donorScreeningMrsaHistory,
        donorScreeningIsolationHistory,
        donorScreeningPotentialTissueIssue,
        donorScreeningPotentialTissueIssueNote,
        donorScreeningSignsOfInfection,
        donorScreeningSignsOfInfectionNotes,
        donorScreeningActiveSepsis,
        donorScreeningActiveSepsisNotes,
        donorScreeningLastDocumentedDate,
        donorScreeningLastDocumentedTime,
        donorScreeningLastDocumentedNote,
        nokSalutation,
        nokFirstName,
        nokLastName,
        nokName,
        nokRelationToDonor,
        nokAddress,
        nokCity,
        nokState,
        nokZip,
        nokPhone,
        nokAlternatePhone,
        nokAwareOfLocationOfBody,
        nokNotifiedOfDeath,
        nok2FirstName,
        nok2LastName,
        nok2Name,
        nok2RelationToDonor,
        nok2Address,
        nok2City,
        nok2State,
        nok2Zip,
        nok2Phone,
        nok2AlternatePhone,
        facilityHospital,
        referralCreatedOn,
        nokEmailAddress,
        funeralHome,
        funeralHomeCity,
        funeralHomePhone,
        yesNoOptions,
        ocularTeam,
        ocularTeamDateTime,
        ocularTeamAcknowledged,
        ocularTeamAcknowledgedDateTime,
        familyStatus,
        familyStatusUpdatedBy,
        familyStatusUpdatedOn,
        organStatus,
        organStatusUpdatedBy,
        organStatusUpdatedOn,
        tissueStatus,
        tissueStatusUpdatedBy,
        tissueStatusUpdatedOn,
        disableImport,
        pendingDfsRnUpdates,
        hideFromDashboard,
        callNotes,
        salutationOptions,
        meContactedOnDate,
        meContactedOnTime,
        mePreOrPostMortemLabel,
        meOffice,
        ocMorgueDate,
        funeralHomeContactedOnDate,
        funeralHomeContactedOnTime,
        recentAlbuminGiven,
        recentAlbuminAmount,
        recentIVMedsGiven,
        recentIVMedsAmount,
        recentIVFluidGiven,
        recentIVFluidAmount,
        recentBloodGiven,
        recentBloodAmount,
        plasmaLast48Hours,
        recentPlasmaAmount,
        prelimComments,
        transfusedRbcAmount,
        transfusedWholeBloodAmount,
        transfusedOtherAmount,
        colloidsHespanAmount,
        colloidsFfpAmount,
        colloidsPlateletsAmount,
        colloidsCryoAmount,
        colloidsAlbumin5Amount,
        colloidsAlbumin25Amount,
        colloidsOtherAmount,
        crystalloidsAmount,
        crystalloidsOtherAmount,
        initialMentionBy,
        initialMentionMode,
        approachInitialMentionFamilyResponse,
        initatedByFirstName,
        initiatedByLastName,
        initiatedByTitle,
        initiatedOnDateTime,
        initialMentionRestrictions,
        initialMentionNa,
        initialMentionComments,
        highRiskNotes,
        consentNotRequested,
        consentNotRequestedReason,
        approachType,
        approachMode,
        formalRequestBy,
        formalRequestTitle,
        requestMadeByOther,
        formalRequestDateTime,
        formalRequestRestrictions,
        formalRequestCommentsNA,
        formalRequestComments,
        tissueOutcome,
        tissueOutcomeDetail,
        eyeOutcome,
        isReferredOnVent,
        donorReferredOnVent,
        donorVented24HrsPrior,
    };
};
